import React from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import DataConnector from 'connect/DataConnector';
import {StorableItemType} from 'constants/ModelTypeConstants';
import {SelectedParentStorable} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import ParentStorableDataSelector from 'samples/ParentStorableDataSelector';
import * as SampleUtil from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {restStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

export interface SelectParentStorableModalProps {
  modalTitleText: string;
  onRequestClose: () => void;
  onSubmit?: (
    selectedParent: SelectedParentStorable,
    eventGroupId: string,
    onFinish?: (errorMessage?: string) => void
  ) => void;
  /** If true, modal will wait for onFinish to be called from inside `onSetParentStorable` before closing.
   * If called with an error message, modal will remain up to render that error. Otherwise, it will close.
   * If false, modal will close immediately on submit and any errors will have to be surfaced via toasts. */
  waitForSubmitErrors?: boolean;
  parentStorableTypesOverride?: Array<StorableItemType>;
  storableSchema?: {id: string; registryId: string; name: string} | null;
  submitText: string;
  sampleStatusWarningParams?: Omit<SampleStatusWarningTrackingParams, 'eventGroupId'>;
  initialSelectedParentId?: string | null;
  allowNone?: boolean;
}

interface EnhancedSelectParentStorableModalProps {
  initialSelectedParent?: SelectedParentStorable;
}

interface State {
  selectedParent: SelectedParentStorable;
  canProceedOnSampleStatus: boolean;
  selectedSampleIds: Array<string>;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  sampleStatusWarningsLoading: boolean;
  eventGroupId: string;
  isSubmitting: boolean;
  submitErrorMessage: string | null;
}

class SelectParentStorableModalConnector extends DataConnector<
  SelectParentStorableModalProps,
  EnhancedSelectParentStorableModalProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps {
    if (this.props.initialSelectedParentId == null) {
      return this.props;
    }

    const hasInitialSelectedParentStorableSummaryExtras = this.storableStore()
      .getStorableSummaryExtras()
      .ancestralStorableIdsByStorableId?.hasOwnProperty(this.props.initialSelectedParentId);

    if (!hasInitialSelectedParentStorableSummaryExtras) {
      StorableSummaryExtrasManager.requestFetch(this.props.initialSelectedParentId);
    }

    const initialSelectedParentStorableSummary = this.storableStore().getStorableSummary(
      this.props.initialSelectedParentId
    );
    if (initialSelectedParentStorableSummary == null) {
      StorableActions.bulkFetchSummaries([this.props.initialSelectedParentId]);
    }

    if (initialSelectedParentStorableSummary != null && hasInitialSelectedParentStorableSummaryExtras) {
      const initialSelectedParent: SelectedParentStorable = {
        storable: restStorableSummaryToGraphQL(initialSelectedParentStorableSummary),
        gridCells: [],
      };
      return {
        ...this.props,
        initialSelectedParent,
      };
    } else {
      return this.props;
    }
  }
}

export class SelectParentStorableModal extends React.Component<
  SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps
> {
  override state: State = {
    selectedParent: getEmptySelectedParent(),
    canProceedOnSampleStatus: false,
    selectedSampleIds: [],
    shouldShowStatusWarnings: false,
    samples: null,
    sampleStatusWarningsLoading: this.props.sampleStatusWarningParams != null,
    eventGroupId: makeAnalyticsEventId(),
    isSubmitting: false,
    submitErrorMessage: null,
  };

  async checkForWarnings(): Promise<void> {
    const sampleIds = this.props.sampleStatusWarningParams?.sampleIds ?? [];
    const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
      sampleIds,
    });
    this.setState({shouldShowStatusWarnings: shouldShowWarning, samples: samples ?? null});
    this.setSelectedSampleIds(sampleIds);
    this.setState({sampleStatusWarningsLoading: false});
  }

  private setCanProceedOnSampleStatus = (override: boolean): void => {
    this.setState({canProceedOnSampleStatus: override});
  };

  private setSelectedSampleIds = (sampleIds: Array<string>): void => {
    this.setState({selectedSampleIds: sampleIds});
  };

  private setSelectedParent = (selectedParent: SelectedParentStorable): void => {
    this.setState({selectedParent});
  };

  override componentDidMount(): void {
    if (this.props.sampleStatusWarningParams != null) {
      this.checkForWarnings();
    }
    if (this.state.selectedParent.storable == null && this.props.initialSelectedParent != null) {
      this.setSelectedParent(this.props.initialSelectedParent);
    }
  }

  override componentDidUpdate(
    prevProps: Readonly<SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps>,
    prevState: State
  ): void {
    if (
      this.state.selectedParent.storable == null &&
      this.props.initialSelectedParent &&
      (prevProps.initialSelectedParent == null ||
        prevProps.initialSelectedParentId !== this.props.initialSelectedParentId)
    ) {
      this.setSelectedParent(this.props.initialSelectedParent);
    }
    if (prevState.isSubmitting && !this.state.isSubmitting && !this.state.submitErrorMessage) {
      prevProps.onRequestClose();
    }
  }

  private isLoading(): boolean {
    return (
      this.state.sampleStatusWarningsLoading ||
      (this.props.initialSelectedParentId != null && this.props.initialSelectedParent == null)
    );
  }

  private shouldDisableSave(): boolean {
    const {storable, gridCells} = this.state.selectedParent;
    return (
      (storable ? SampleUtil.isGridId(storable.id) && gridCells.length === 0 : !this.props.allowNone) ||
      this.isLoading() ||
      this.state.isSubmitting ||
      !canProceedWithSamples(
        this.state.selectedSampleIds,
        this.state.shouldShowStatusWarnings,
        this.state.canProceedOnSampleStatus
      )
    );
  }

  private handleSubmit = (): void => {
    const {onSubmit} = this.props;
    if (onSubmit) {
      this.setState({isSubmitting: true, submitErrorMessage: null});
      onSubmit(
        this.state.selectedParent,
        this.state.eventGroupId,
        this.props.waitForSubmitErrors ? this.handleCompleteSubmitAttempt : undefined
      );
    }
    if (!this.props.waitForSubmitErrors) {
      this.handleCancel();
    }
  };

  private handleCompleteSubmitAttempt = (errorMessage?: string): void => {
    this.setState({isSubmitting: false, submitErrorMessage: errorMessage ?? null});
  };

  handleCancel = (): void => {
    this.props.onRequestClose();
  };

  private renderPossibleWarning = (): JSX.Element | null => {
    if (this.props.sampleStatusWarningParams == null) {
      return null;
    }
    const {sampleIds, ...statusWarningTrackingParams} = this.props.sampleStatusWarningParams;
    return this.state.shouldShowStatusWarnings && this.state.samples != null ? (
      <SampleStatusWarning
        sampleIds={sampleIds}
        selectedSampleIds={this.state.selectedSampleIds}
        setSelectedSampleIds={this.setSelectedSampleIds}
        shouldProceed={this.state.canProceedOnSampleStatus}
        setShouldProceed={this.setCanProceedOnSampleStatus}
        trackingParams={{...statusWarningTrackingParams, eventGroupId: this.state.eventGroupId}}
        samples={this.state.samples}
      />
    ) : null;
  };

  override render(): JSX.Element {
    return (
      <Modal
        title={this.props.modalTitleText}
        onRequestClose={this.handleCancel}
        onRequestSubmit={this.handleSubmit}
        buttons={[
          {
            text: this.state.isSubmitting ? 'Saving...' : this.props.submitText,
            disabled: this.shouldDisableSave(),
            submit: true,
            style: ButtonStyle.PRIMARY,
          },
        ]}
      >
        {this.isLoading() ? (
          <CenteredHorizontalThrobber />
        ) : (
          <>
            {this.renderPossibleWarning()}
            {canProceedWithSamples(
              this.state.selectedSampleIds,
              this.state.shouldShowStatusWarnings,
              this.state.canProceedOnSampleStatus
            ) && (
              <ParentStorableDataSelector
                onUpdate={this.setSelectedParent}
                autoFocus={true}
                parentStorableTypesOverride={this.props.parentStorableTypesOverride}
                selectedParent={this.state.selectedParent}
                storableSchema={this.props.storableSchema}
                allowNone={this.props.allowNone}
                // storablesToBeMoved is undefined for locations since they don't count against available space
                storablesToBeMoved={
                  this.props.storableSchema && APIUtil.isLocationSchema(this.props.storableSchema.id)
                    ? undefined
                    : [{storableSchema: this.props.storableSchema ?? undefined}]
                }
              />
            )}
            {this.state.submitErrorMessage && (
              <InlineBanner bannerType={InlineBannerType.ERROR} text={this.state.submitErrorMessage} />
            )}
          </>
        )}
      </Modal>
    );
  }
}

const ConnectedSelectParentStorableModal =
  SelectParentStorableModalConnector.connect(SelectParentStorableModal);
export default ConnectedSelectParentStorableModal;
